import { useEffect } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Asterisk, Home, Phone } from "lucide-react";
import { useForm } from "react-hook-form";
import type { SubmitHandler } from "react-hook-form";

import { useMultiStepFormStore } from "@/stores";
import { addressInformationSchema } from "@/types";
import type { AddressInformationFormType } from "@/types";
import { ControlledRadioGroupCard, FormField, Input } from "@/ui";
import { YES_NO_OPTIONS, YES_NO_RADIO_OPTIONS } from "@/utils";
import { AddressAccordion } from "./AddressAccordion";
import FormButtons from "./FormButtons";

const AddressInformationForm = () => {
  const { setMultiStepFormData, goToNextFormStep, multiStepFormData } =
    useMultiStepFormStore();

  const {
    register,
    handleSubmit,
    control,
    watch,
    resetField,
    reset,
    formState: { errors },
  } = useForm<AddressInformationFormType>({
    resolver: zodResolver(addressInformationSchema),
    defaultValues: {
      hasSameShippingAddress: YES_NO_OPTIONS.YES,
    },
  });
  const onSubmit: SubmitHandler<AddressInformationFormType> = (data) => {
    setMultiStepFormData({
      addressInformationFormData: data,
    });
    goToNextFormStep();
  };
  const hasSameShippingAddress = watch("hasSameShippingAddress");

  useEffect(() => {
    if (multiStepFormData?.addressInformationFormData) {
      reset(multiStepFormData?.addressInformationFormData);
    }
  }, [reset, multiStepFormData?.addressInformationFormData]);

  useEffect(() => {
    if (hasSameShippingAddress === YES_NO_OPTIONS.YES) {
      resetField("shippingAddress");
    }
  }, [hasSameShippingAddress, resetField]);
  return (
    <div className="flex flex-col gap-y-10">
      <div className="flex justify-between">
        <div className="flex items-center gap-2">
          <Home className="text-primary-600" size={16} />
          <p className="font-extrabold">Address information</p>
        </div>
        <p className="flex text-sm text-neutral-500">
          Required fields (<Asterisk className="text-primary-600" size={12} />)
        </p>
      </div>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="grid grid-cols-2 gap-x-12 gap-y-10"
      >
        <FormField
          label="Cell-phone number"
          error={errors.cellPhoneNumber?.message}
          hasAsterisk
        >
          <Input
            type="tel"
            placeholder="0000000000"
            {...register("cellPhoneNumber")}
            error={errors.cellPhoneNumber?.message}
            icon={<Phone size={16} />}
          />
        </FormField>
        <AddressAccordion
          title="Billing address"
          fieldPath="billingAddress"
          registerFn={register}
          control={control}
          defaultOpen={true}
          errorMessages={{
            addressLineOne: errors.billingAddress?.addressLineOne?.message,
            addressLineTwo: errors.billingAddress?.addressLineTwo?.message,
            city: errors.billingAddress?.city?.message,
            state: errors.billingAddress?.state?.message,
            zipCode: errors.billingAddress?.zipCode?.message,
          }}
        />
        <FormField
          label={"Same as Billing Address?"}
          error={errors.hasSameShippingAddress?.message}
          hasAsterisk
        >
          <ControlledRadioGroupCard
            name="hasSameShippingAddress"
            control={control}
            options={YES_NO_RADIO_OPTIONS}
          />
        </FormField>
        {hasSameShippingAddress === YES_NO_OPTIONS.NO && (
          <AddressAccordion
            title="Shipping address"
            fieldPath="shippingAddress"
            registerFn={register}
            control={control}
            errorMessages={{
              addressLineOne: errors.shippingAddress?.addressLineOne?.message,
              addressLineTwo: errors.shippingAddress?.addressLineTwo?.message,
              city: errors.shippingAddress?.city?.message,
              state: errors.shippingAddress?.state?.message,
              zipCode: errors.shippingAddress?.zipCode?.message,
            }}
          />
        )}
        <FormButtons />
      </form>
    </div>
  );
};

export default AddressInformationForm;
